import {
  json,
  redirect,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import {
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useLocation,
  useMatches,
  useRouteError,
} from '@remix-run/react'
import {
  captureRemixErrorBoundaryError,
  setUser as setSentryUser,
  withSentry,
} from '@sentry/remix'
import { QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/remix'
import { Provider, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import posthog from 'posthog-js'
import { useEffect, useMemo, useState } from 'react'
import type { EmbedWithBroker } from '~/atoms'
import {
  aCookieContact,
  aEmbed,
  aInitialMapSearch,
  aOrganization,
  aOriginalLoginUser,
  aUser,
} from '~/atoms'
import { Toaster } from '~/components/ui'
import type {
  CookieContact,
  ModelUser,
  OrganizationOptionsInferred,
  User,
} from '~/models'
import { findEmbed, findUser, getRootOrganization } from '~/mongo.server'
import { cn, createIdbPersister } from '~/utils'
import {
  contactCookie,
  deleteCookieTokenHeader,
  embedCookie,
  getCookieToken,
  getOrganizationSlug,
  hasHeaderToken,
  parseTokenUser,
} from '~/utils.server'
import env from '~/utils.server/environment.server'
import { RouteChangeProvider, useEmbed } from './hooks'
import styles from './tailwind.css?url'
import { TRPCProvider } from './trpc/client'
import {
  getQueryClient,
  getTRPCClient,
  PERSISTED_QUERY_MAX_AGE,
} from './trpc/react-query'

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl }) => {
  return (
    currentUrl.pathname === '/login' ||
    currentUrl.pathname === '/redirect' ||
    currentUrl.pathname === '/admin/profile' ||
    currentUrl.pathname === '/admin/settings'
  )
}

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Source+Sans+3:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
  },
  {
    rel: 'icon',
    href: '/favicon.ico',
    sizes: 'any',
  },
  {
    rel: 'apple-touch-icon',
    href: '/apple-touch-icon.png',
  },
]

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: 'Associate' },
    {
      property: 'og:image',
      content: data?.organization?.meta.share
        ? data.organization.meta.share.url
        : '/open-graph.png',
    },
  ]
}

type LoaderData = {
  organization: OrganizationOptionsInferred
  user?: User
  originalLoginUser?: ModelUser
  cookieContact?: CookieContact
  embed?: EmbedWithBroker
  clientEnv: ClientEnv
  initialLocationSearch?: string | null
}

type ClientEnv = {
  GOOGLE_MAPS_API_KEY: string
}

declare global {
  interface Window {
    ENV?: ClientEnv
    Intercom?: (action: string, ...args: unknown[]) => void
    intercomSettings?: IntercomSettings
  }
}

type IntercomSettings = {
  Group: string
  Role: string
  Subdomain: string
  api_base: string
  app_id: string
  email: string
  name: string
  user_id: string
}

const INTERCOM_APP_ID = 'a3v8n6l5'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  let slug = getOrganizationSlug(request)

  const organization = await getRootOrganization({ slug })

  if (!organization) {
    throw new Response('Not Found', { status: 404 })
  }

  const clientEnv: ClientEnv = {
    GOOGLE_MAPS_API_KEY: env.GOOGLE_MAPS_API_KEY,
  }

  if (
    request.url.includes('/login') ||
    request.url.includes('/redirect') ||
    request.url.includes('/forgot-password')
  ) {
    return {
      organization,
      originalUrl: request.url,
      clientEnv,
    }
  }

  const { pathname, searchParams } = new URL(request.url)
  const redirectParam =
    pathname !== '/' && pathname !== '/login' ? `?redirect=${pathname}` : ''

  const token = await getCookieToken(request)
  if (
    request.url.includes('/admin') &&
    (!hasHeaderToken(request) || token === null)
  ) {
    return redirect(`/login${redirectParam}`)
  }

  let user = null
  let originalLoginUser = null
  if (token !== null) {
    const parsedUser = parseTokenUser(token)
    if (parsedUser !== null) {
      user = await findUser({
        _id: parsedUser._id,
        'organization._id': organization._id,
      })
      originalLoginUser = parsedUser.originalLoginUser

      if (user!.status === 'suspended' || user!.status === 'inactive') {
        return redirect(
          `/login?message=Your account is suspended or no longer active. Please contact your organization to inquire about your account.`,
          {
            headers: await deleteCookieTokenHeader(),
          }
        )
      }
    } else {
      return redirect(`/login${redirectParam}`, {
        headers: await deleteCookieTokenHeader(),
      })
    }
  }

  // Members Only Organization
  if (organization.options.membersOnly && !user) {
    return redirect(`/login${redirectParam}`)
  }

  const cookieContact = await contactCookie.parse(
    request.headers.get('Cookie') || ''
  )

  let embed: EmbedWithBroker | undefined
  const embedSlug = searchParams.get('embedSlug')
  const hostURL = searchParams.get('hostURL')
  const broker = searchParams.get('broker')

  if (embedSlug) {
    embed =
      (await findEmbed({
        filter: {
          'organization.slug': organization.slug,
          slug: embedSlug,
        },
      })) ?? undefined
  }

  if (embed) {
    if (hostURL) embed.hostURL = hostURL
    if (broker) embed.broker = broker
  }

  const data = {
    embed,
    organization,
    user,
    originalLoginUser,
    cookieContact,
    originalUrl: request.url,
    clientEnv,
    initialLocationSearch: searchParams.toString() || null,
  }

  if (!embedSlug) {
    return json(data, {
      headers: {
        'Set-Cookie': await embedCookie.serialize('', { maxAge: 0 }),
      },
    })
  }

  return json(data)
}

function DebugScreenSizes() {
  if (!import.meta.env.DEV) {
    return null
  }
  return (
    <div className="fixed left-0 top-0 z-[999] bg-red-500 px-2 py-1 text-white print:hidden">
      <span className="sm:hidden">xs</span>
      <span className="hidden sm:inline-block md:hidden">sm</span>
      <span className="hidden md:inline-block lg:hidden">md</span>
      <span className="hidden lg:inline-block xl:hidden">lg</span>
      <span className="hidden xl:inline-block 2xl:hidden">xl</span>
      <span className="hidden 2xl:inline-block">2xl</span>
    </div>
  )
}

function getClientRuntimeScript(clientEnv?: ClientEnv) {
  const envScript = clientEnv
    ? `window.ENV = ${JSON.stringify(clientEnv).replace(/</g, '\\u003c')};`
    : ''

  return `${envScript}
window.addEventListener("beforeunload", function () {
  try {
    sessionStorage.removeItem("positions");
  } catch (_) {}
});`
}

function HydrationContainer({
  organization,
  user,
  originalLoginUser,
  cookieContact,
  embed,
  initialLocationSearch,
}: {
  organization: OrganizationOptionsInferred
  user?: User
  originalLoginUser?: ModelUser
  cookieContact?: CookieContact
  embed?: EmbedWithBroker
  initialLocationSearch?: string | null
}) {
  useHydrateAtoms([[aOrganization, organization]])
  useHydrateAtoms([[aUser, user ?? null]])
  useHydrateAtoms([[aOriginalLoginUser, originalLoginUser ?? null]])
  useHydrateAtoms([[aCookieContact, cookieContact ?? null]])
  useHydrateAtoms([[aEmbed, embed ?? null]])
  useHydrateAtoms([[aInitialMapSearch, initialLocationSearch ?? null]])

  const setUser = useSetAtom(aUser)
  const setOriginalLoginUser = useSetAtom(aOriginalLoginUser)

  useEffect(() => {
    setUser(user ?? null)
    setOriginalLoginUser(originalLoginUser ?? null)
  }, [user])

  return <Outlet />
}

function Document({
  organization,
  user,
  clientEnv,
  children,
}: {
  organization?: OrganizationOptionsInferred
  user?: User
  clientEnv?: ClientEnv
  children: React.ReactNode
}) {
  const { pathname } = useLocation()
  const preventSearchEngineIndexing = useMemo(() => {
    if (organization?.options?.preventSearchEngineIndexing) {
      return true
    } else if (
      pathname.startsWith('/admin') ||
      pathname.startsWith('/reports')
    ) {
      return true
    }
    return false
  }, [organization, pathname])

  const canonicalUrl = useMemo(() => {
    if (
      organization?.options.wordpressSiteUrl &&
      pathname.startsWith('/listings/')
    ) {
      return `${organization!.options.wordpressSiteUrl}${pathname}`.replace(
        '//listings',
        '/listings'
      )
    }
    return null
  }, [organization, pathname])

  return (
    <html lang="en" className="scroll-pt-40 overflow-x-hidden">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        {preventSearchEngineIndexing && (
          <meta name="robots" content="noindex, nofollow, noarchive" />
        )}
        <Links />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </head>
      <body
        className={cn(
          '!opacity-100 transition-opacity duration-300',
          user && 'logged-in'
        )}
        style={{ opacity: 0 }}>
        <RouteChangeProvider>{children}</RouteChangeProvider>
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: getClientRuntimeScript(clientEnv),
          }}
        />
        <ScrollRestoration />
        <Scripts />

        {import.meta.env.PROD && (
          <>
            <Analytics />
            <SpeedInsights />
          </>
        )}
      </body>
    </html>
  )
}

function IntercomBoot({
  organization,
  user,
}: {
  organization: OrganizationOptionsInferred
  user?: User
}) {
  useEffect(() => {
    if (!user || !organization.options.intercomEnabled) {
      window.Intercom?.('shutdown')
      return
    }

    const settings: IntercomSettings = {
      api_base: 'https://api-iam.intercom.io',
      app_id: INTERCOM_APP_ID,
      name: `${user.firstName} ${user.lastName}`,
      user_id: user._id,
      email: user.email,
      Subdomain: organization.slug,
      Role: user.role ?? '',
      Group: user.group,
    }

    window.intercomSettings = settings

    if (!window.Intercom) {
      const queuedIntercom = ((action: string, ...args: unknown[]) => {
        queuedIntercom.q.push([action, ...args])
      }) as Window['Intercom'] & { q: unknown[][] }
      queuedIntercom.q = []
      window.Intercom = queuedIntercom
    }

    if (!document.getElementById('intercom-widget-script')) {
      const script = document.createElement('script')
      script.id = 'intercom-widget-script'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
      document.body.appendChild(script)
    }

    window.Intercom('boot', settings)

    return () => {
      window.Intercom?.('shutdown')
    }
  }, [organization.options.intercomEnabled, organization.slug, user])

  return null
}

function AppLayout() {
  const {
    organization,
    user,
    originalLoginUser,
    cookieContact,
    embed,
    clientEnv,
    initialLocationSearch,
  } = useLoaderData<LoaderData>()
  const location = useLocation()
  const { isEmbed } = useEmbed()
  const [queryClient] = useState(() => getQueryClient())
  const [trpcClient] = useState(() => getTRPCClient())

  useEffect(() => {
    posthog.capture('$pageview')
  }, [location.pathname])

  useEffect(() => {
    if (user) {
      // Test original logged in user
      posthog.identify(user.tid, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      })
    }
  }, [user])

  useEffect(() => {
    if (user) {
      setSentryUser({ email: user.email })
    } else {
      setSentryUser(null)
    }
  }, [user])

  useEffect(() => {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.width = '100px'
    document.body.appendChild(outer)

    const widthNoScroll = outer.offsetWidth
    outer.style.overflow = 'scroll'

    const inner = document.createElement('div')
    inner.style.width = '100%'
    outer.appendChild(inner)

    const widthWithScroll = inner.offsetWidth
    outer.parentNode?.removeChild(outer)

    if (widthNoScroll - widthWithScroll > 0) {
      document.documentElement.classList.add('scrollbar')
    }
  }, [])

  return (
    <Document organization={organization} user={user} clientEnv={clientEnv}>
      <DebugScreenSizes />
      <Provider>
        {isEmbed ? (
          <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
              <HydrationContainer
                organization={organization}
                user={user}
                originalLoginUser={originalLoginUser}
                cookieContact={cookieContact}
                embed={embed}
                initialLocationSearch={initialLocationSearch}
              />
            </TRPCProvider>
          </QueryClientProvider>
        ) : (
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister: createIdbPersister(),
              maxAge: PERSISTED_QUERY_MAX_AGE,
            }}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
              <HydrationContainer
                organization={organization}
                user={user}
                originalLoginUser={originalLoginUser}
                cookieContact={cookieContact}
                embed={embed}
                initialLocationSearch={initialLocationSearch}
              />
            </TRPCProvider>
          </PersistQueryClientProvider>
        )}
      </Provider>
      <IntercomBoot organization={organization} user={user} />
      <Toaster />
    </Document>
  )
}

function EmptyLayout() {
  return <Outlet />
}

function App() {
  const matches = useMatches()

  const useEmptyLayout = useMemo(() => {
    return matches.some((match) => {
      return (
        match.handle !== null &&
        typeof match.handle === 'object' &&
        'useEmptyLayout' in match.handle &&
        match.handle.useEmptyLayout === true
      )
    })
  }, [matches])

  if (useEmptyLayout) {
    return <EmptyLayout />
  } else {
    return <AppLayout />
  }
}

export default withSentry(App)

export function ErrorBoundary() {
  const error = useRouteError()
  const renderError = () => {
    if (isRouteErrorResponse(error)) {
      return (
        <div className="mx-auto mt-10 flex flex-col justify-around px-4 py-8 text-center ">
          <h1 className="my-4 text-2xl">
            {error.status} {error.statusText}
          </h1>

          <p className="py-4">
            The page you requested does not exist. Click{' '}
            <Link viewTransition to="/admin" className="underline">
              here
            </Link>{' '}
            to return to the dashboard.
          </p>
        </div>
      )
    } else if (error instanceof Error) {
      return (
        <div>
          <h1>Error</h1>
          <p>{error.message}</p>
          <p>The stack trace is:</p>
          <pre>{error.stack}</pre>
        </div>
      )
    } else {
      return <h1>Unknown Error</h1>
    }
  }
  if (import.meta.env.PROD) {
    captureRemixErrorBoundaryError(error)
  }
  return <Document>{renderError()}</Document>
}
